/**
 * AgentMessage - Minimalist mission control styled agent messages
 *
 * Features:
 * - Compact layout with subtle role indicator
 * - Markdown rendering for rich content
 * - Workflow step visualization
 * - Interactive CDM selection from list_cdms tool results
 * - Retro mission control aesthetic
 */

import { memo, useMemo } from 'react'
import { ChatMessage, ToolStatus } from '../../types/chat'
import { extractCdmList, type CdmSummary } from '../../types/cdm'
import { FeedbackReaction } from '../../types/feedback'
import { createReactionHandler, type ReactionDispatch } from '../../lib/feedback'
import MarkdownRenderer from './MarkdownRenderer'
import WorkflowVisualizer from './WorkflowVisualizer'
import CdmSelectionList from './CdmSelectionList'
import ReactionBar from '../Feedback/ReactionBar'

interface AgentMessageProps {
  message: ChatMessage
  reaction?: FeedbackReaction | null
  createDispatch?: (messageId: string | null) => ReactionDispatch
  onPlanManeuver?: (cdmId: string) => void
  disabled?: boolean
}

function AgentMessage({
  message,
  reaction = null,
  createDispatch,
  onPlanManeuver,
  disabled = false,
}: AgentMessageProps) {
  // Extract CDM list data from completed workflow steps
  const cdmList: CdmSummary[] = useMemo(() => {
    if (!message.workflow_steps) return []
    for (const step of message.workflow_steps) {
      if (step.status === ToolStatus.COMPLETE && step.response) {
        const cdms = extractCdmList(step.tool, step.response)
        if (cdms && cdms.length > 0) return cdms
      }
    }
    return []
  }, [message.workflow_steps])

  return (
    <div className="group">
      {/* Role indicator - subtle, inline */}
      <div className="flex items-center gap-2 mb-1">
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: '#66cccc' }}
        />
        <span className="text-xs font-mono uppercase tracking-wider text-retro-dim">
          copilot
        </span>
      </div>

      {/* Message content */}
      <div className="pl-3.5 border-l border-retro-border">
        <MarkdownRenderer content={message.content} />

        {/* Interactive CDM selection list (from list_cdms results) */}
        {cdmList.length > 0 && onPlanManeuver && (
          <CdmSelectionList
            cdms={cdmList}
            onPlanManeuver={onPlanManeuver}
            disabled={disabled}
          />
        )}

        {/* Workflow steps */}
        {message.workflow_steps && message.workflow_steps.length > 0 && (
          <WorkflowVisualizer steps={message.workflow_steps} />
        )}

        {/* Reaction bar — message-level feedback */}
        {message.id && createDispatch && (
          <ReactionBar
            testId={`reaction-bar-${message.id}`}
            reaction={reaction}
            handler={createReactionHandler(
              { sessionId: message.session_id, messageId: message.id },
              createDispatch(message.id),
            )}
          />
        )}
      </div>
    </div>
  )
}

// Memoize so a single thumb click doesn't re-render all sibling messages.
export default memo(AgentMessage, (prev, next) => {
  return (
    prev.message === next.message &&
    prev.reaction === next.reaction &&
    prev.createDispatch === next.createDispatch &&
    prev.onPlanManeuver === next.onPlanManeuver &&
    prev.disabled === next.disabled
  )
})
